import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Download as DownloadIcon } from "@mui/icons-material";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { motion } from "framer-motion";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  Stack,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  MenuItem,
  TablePagination,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Event as EventIcon,
  Celebration as CelebrationIcon,
  Description as DescriptionIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  fetchHolidays,
  deleteExistingHoliday,
} from "store/features/company/leaveManagement/holiday/holidayActions";
import {
  selectHolidays,
  selectHolidayLoading,
} from "store/features/company/leaveManagement/holiday/holidaySelectors";
import AddHolidayModal from "./AddHolidayModal";
import EditHolidayModal from "./EditHolidayModal";
import holidaysSvg from "assets/folders/holidays.svg";
import usePermissions from "hooks/usePermissions";

const Holiday = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const holidays = useSelector(selectHolidays);
  const loading = useSelector(selectHolidayLoading);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [holidayToDelete, setHolidayToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [yearFilter, setYearFilter] = useState(
    new Date().getFullYear().toString(),
  );

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const { hasPermission, hasAnyPermission } = usePermissions();

  useEffect(() => {
    dispatch(fetchHolidays());
  }, [dispatch]);

  const getYearFromDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.getFullYear();
  };

  const currentYear = new Date().getFullYear();
  const dynamicYears = [];
  for (let y = currentYear + 5; y >= currentYear - 5; y--) {
    dynamicYears.push(y);
  }

  const availableYears = [
    ...new Set([
      ...dynamicYears,
      ...holidays.map((h) => getYearFromDate(h.holiday_date)).filter((y) => y),
    ]),
  ].sort((a, b) => b - a);

  const filteredHolidays = holidays.filter((holiday) => {
    const matchesSearch =
      searchTerm === "" ||
      holiday.holiday_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      holiday.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesYear =
      yearFilter === "all" ||
      getYearFromDate(holiday.holiday_date) === parseInt(yearFilter);

    return matchesSearch && matchesYear;
  });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const HolidayTypeBadge = ({ type }) => {
    if (type === "public") {
      return (
        <Chip
          icon={<CelebrationIcon sx={{ fontSize: 14 }} />}
          label="Public Holiday"
          size="small"
          sx={{
            backgroundColor: "#d1fae5",
            color: "#065f46",
            fontWeight: 500,
            fontSize: "0.75rem",
            height: "24px",
            ...(theme.palette.mode === "dark" && {
              backgroundColor: "rgba(16,185,129,0.25)",
              color: "#6ee7b7",
            }),
          }}
        />
      );
    }
    return (
      <Chip
        icon={<EventIcon sx={{ fontSize: 14 }} />}
        label="Company Holiday"
        size="small"
        sx={{
          backgroundColor: "#dbeafe",
          color: "#1e40af",
          fontWeight: 500,
          fontSize: "0.75rem",
          height: "24px",
          ...(theme.palette.mode === "dark" && {
            backgroundColor: "rgba(59,130,246,0.25)",
            color: "#93c5fd",
          }),
        }}
      />
    );
  };

  const handleDeleteClick = (holiday) => {
    setHolidayToDelete(holiday);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (holidayToDelete) {
      setDeleting(true);
      const result = await dispatch(deleteExistingHoliday(holidayToDelete.id));
      if (result.success) {
        dispatch(fetchHolidays());
        setDeleteDialogOpen(false);
        setHolidayToDelete(null);
      }
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setHolidayToDelete(null);
  };

  if (!hasPermission("holiday.view")) {
    return null;
  }
  const exportHolidaysToExcel = () => {
    if (!filteredHolidays || filteredHolidays.length === 0) {
      alert("No data to export");
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([]);

    // Yellow header style
    const yellowHeaderStyle = {
      font: {
        bold: true,
        sz: 12,
        color: { rgb: "000000" },
      },
      fill: {
        fgColor: { rgb: "FFFF00" },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    let row = 0;

    // Title
    XLSX.utils.sheet_add_aoa(ws, [["HOLIDAYS REPORT"]], {
      origin: `A${row + 1}`,
    });
    ws["!merges"] = [{ s: { r: row, c: 0 }, e: { r: row, c: 5 } }];
    ws[`A${row + 1}`].s = {
      font: { bold: true, sz: 14, color: { rgb: "000000" } },
      fill: { fgColor: { rgb: "FFFF00" } },
      alignment: { horizontal: "center", vertical: "center" },
    };
    row += 2;

    // Summary
    XLSX.utils.sheet_add_aoa(ws, [["SUMMARY"]], { origin: `A${row + 1}` });
    ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 1 } });
    ws[`A${row + 1}`].s = yellowHeaderStyle;
    row++;

    XLSX.utils.sheet_add_aoa(
      ws,
      [
        ["Metric", "Value"],
        ["Total Holidays", filteredHolidays.length],
        [
          "Public Holidays",
          filteredHolidays.filter((h) => h.holiday_type === "public").length,
        ],
        [
          "Company Holidays",
          filteredHolidays.filter((h) => h.holiday_type === "company").length,
        ],
      ],
      { origin: `A${row + 1}` },
    );
    row += 6;

    // Headers
    const headers = [
      "ID",
      "Holiday Name",
      "Date",
      "Holiday Type",
      "Description",
      "Created At",
    ];
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: `A${row + 1}` });

    // Apply yellow header style to header row
    for (let i = 0; i < headers.length; i++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: i });
      if (ws[cellRef]) {
        ws[cellRef].s = yellowHeaderStyle;
      }
    }
    row++;

    // Data
    filteredHolidays.forEach((holiday) => {
      XLSX.utils.sheet_add_aoa(
        ws,
        [
          [
            holiday.id || "",
            holiday.holiday_name || "",
            holiday.holiday_date ? formatDate(holiday.holiday_date) : "",
            holiday.holiday_type || "",
            holiday.description || "",
            holiday.created_at ? formatDateTime(holiday.created_at) : "",
          ],
        ],
        { origin: `A${row + 1}` },
      );
      row++;
    });

    // Column widths
    ws["!cols"] = headers.map((_, index) => {
      const widths = {
        0: 15, // ID
        1: 30, // Holiday Name
        2: 22, // Date
        3: 20, // Holiday Type
        4: 35, // Description
        5: 25, // Created At
      };
      return { wch: widths[index] || 20 };
    });

    XLSX.utils.book_append_sheet(wb, ws, "Holidays");

    // Write file
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(
      blob,
      `Holidays_Report_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };
  return (
    <Box
      sx={{
        minHeight: "100vh",
        // background:
        //   theme.palette.mode === "dark"
        //     ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
        //     : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={holidaysSvg}
              alt="Holidays"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                }}
              >
                Holidays
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                }}
              >
                Manage company holidays and celebrations
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {/* Export Excel Button */}
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportHolidaysToExcel}
              sx={{
                background: "linear-gradient(135deg, #25a55f 0%, #1e8b4e 100%)",
                color: "#ffffff",
                borderRadius: "8px",
                px: 2.5,
                py: 0.5,
                fontSize: "0.8125rem",
                fontWeight: 500,
                textTransform: "none",
                minWidth: "auto",
                whiteSpace: "nowrap",
                boxShadow: "none",
                transition: "all 0.2s ease",
                height: "36px",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1e8b4e 0%, #16723e 100%)",
                  boxShadow: "0 2px 8px rgba(37, 165, 95, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Export Excel
            </Button>

            {hasPermission("holiday.create") && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateModal(true)}
                sx={{
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  borderRadius: "10px",
                  px: 2.5,
                  py: 1,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  textTransform: "none",
                  minWidth: "auto",
                  whiteSpace: "nowrap",
                  boxShadow: "none",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                    boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                Add Holiday
              </Button>
            )}
          </Box>
        </Stack>
      </Box>

      {/* Search and Filter Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3, width: "100%" }}
      >
        <TextField
          placeholder="Search by holiday name or description..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          variant="outlined"
          size="small"
          sx={{
            flex: { xs: "none", sm: "0 0 320px", md: "0 0 400px" },
            width: "100%",
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          label="Year"
          value={yearFilter}
          onChange={(e) => {
            setYearFilter(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{
            minWidth: { xs: "100%", sm: 160 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
            "& .MuiInputLabel-root": {
              color: "text.secondary",
              "&.Mui-focused": {
                color: "primary.main",
              },
            },
          }}
        >
          <MenuItem value="all">All Years</MenuItem>
          {availableYears.map((year) => (
            <MenuItem key={year} value={year.toString()}>
              {year}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress
              size={48}
              sx={{
                color: "primary.main",
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                },
              }}
            />
            <Typography
              sx={{
                color: "text.secondary",
              }}
            >
              Loading holidays...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 10px 25px -5px rgba(0,0,0,0.3)"
                : "0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.02)",
            border: `1px solid ${
              theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
            }`,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 800 }}>
              <TableHead
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Holiday Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Date
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Type
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Description
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      minWidth: 180,
                    }}
                  >
                    Created At
                  </TableCell>
                  {/* {hasAnyPermission(["holiday.update", "holiday.delete"]) && (
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        color: "text.secondary",
                      }}
                    >
                      Actions
                    </TableCell>
                  )} */}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredHolidays.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <CelebrationIcon
                        sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }}
                      />
                      <Typography
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        No holidays found. Click "Add Holiday" to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredHolidays
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((holiday, index) => (
                      <TableRow
                        key={holiday.id}
                        component={motion.tr}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        sx={{
                          "&:hover": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(30,41,59,0.5)"
                                : "#f9fafb",
                          },
                        }}
                      >
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <CelebrationIcon sx={{ color: "primary.main" }} />
                            </Box>
                            <Box>
                              <Typography
                                sx={{
                                  fontWeight: 500,
                                  color: "text.primary",
                                }}
                              >
                                {holiday.holiday_name}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#6b7280"
                                      : "#9ca3af",
                                }}
                              >
                                ID: {holiday.id} | Tenant: {holiday.tenant_id}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <EventIcon
                              sx={{ fontSize: 14, color: "primary.main" }}
                            />
                            <Typography
                              sx={{
                                fontSize: "0.875rem",
                                fontWeight: 500,
                                color: "text.primary",
                              }}
                            >
                              {formatDate(holiday.holiday_date)}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <HolidayTypeBadge type={holiday.holiday_type} />
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <DescriptionIcon
                              sx={{ fontSize: 14, color: "#9ca3af" }}
                            />
                            <Typography
                              sx={{
                                fontSize: "0.875rem",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#d1d5db"
                                    : "#4b5563",
                              }}
                            >
                              {holiday.description || "-"}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <EventIcon
                                sx={{ fontSize: 14, color: "primary.main" }}
                              />
                              <Typography
                                sx={{
                                  fontSize: "0.875rem",
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#d1d5db"
                                      : "#4b5563",
                                }}
                              >
                                {formatDateTime(holiday.created_at)}
                              </Typography>
                            </Stack>
                            {holiday.updated_at &&
                              holiday.updated_at !== holiday.created_at && (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  sx={{ opacity: 0.6 }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "0.7rem",
                                      color: "text.secondary",
                                      ml: 3.5,
                                    }}
                                  >
                                    <b>Updated: </b>{" "}
                                    {formatDateTime(holiday.updated_at)}
                                  </Typography>
                                </Stack>
                              )}
                          </Stack>
                        </TableCell>
                        {/* <TableCell align="center">
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="center"
                          >
                            {hasPermission("holiday.update") && (
                              <IconButton
                                onClick={() => {
                                  setSelectedHoliday(holiday);
                                  setShowEditModal(true);
                                }}
                                size="small"
                                sx={{
                                  color: "primary.main",
                                  "&:hover": {
                                    backgroundColor:
                                      theme.palette.mode === "dark"
                                        ? "rgba(245,158,11,0.2)"
                                        : "#fffbeb",
                                  },
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            )}
                            {hasPermission("holiday.delete") && (
                              <IconButton
                                onClick={() => handleDeleteClick(holiday)}
                                size="small"
                                sx={{
                                  color: "#ef4444",
                                  "&:hover": {
                                    backgroundColor:
                                      theme.palette.mode === "dark"
                                        ? "rgba(239,68,68,0.2)"
                                        : "#fef2f2",
                                  },
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Stack>
                        </TableCell> */}
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Pagination */}
      {!loading && holidays.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredHolidays.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            mt: 2,
            color: "text.secondary",
            ".MuiTablePagination-selectIcon, .MuiTablePagination-actions button":
              {
                color: "text.secondary",
              },
          }}
        />
      )}

      {/* Empty State for No Search Results */}
      {!loading && filteredHolidays.length === 0 && holidays.length > 0 && (
        <Paper
          sx={{
            mt: 4,
            p: 4,
            textAlign: "center",
            borderRadius: "16px",
            bgcolor: "background.paper",
          }}
        >
          <SearchIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
          <Typography
            sx={{
              color: "text.secondary",
              mb: 2,
            }}
          >
            No holidays match your search criteria
          </Typography>
          <Button
            onClick={() => {
              setSearchTerm("");
              setYearFilter("all");
              setPage(0);
            }}
            sx={{
              color: "primary.main",
              textTransform: "none",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(245,158,11,0.1)"
                    : "#fffbeb",
              },
            }}
          >
            Clear filters
          </Button>
        </Paper>
      )}

      {/* Modals */}
      <AddHolidayModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => dispatch(fetchHolidays())}
        holidays={holidays}
      />

      <EditHolidayModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedHoliday(null);
        }}
        holiday={selectedHoliday}
        onSuccess={() => dispatch(fetchHolidays())}
        holidays={holidays}
      />
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        TransitionComponent={motion.div}
        TransitionProps={{
          timeout: 300,
          style: { overflow: "visible" },
        }}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "20px",
            overflow: "hidden",
            backgroundColor:
              theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
            maxWidth: "400px",
            margin: "16px",
            boxShadow: theme.shadows[24],
            position: "relative",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        <motion.div
          initial={{ x: "100%", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: "100%", opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
        >
          {/* Yellow Banner */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              height: "6px",
            }}
          />

          <Box sx={{ p: 3, position: "relative" }}>
            {/* Close Icon Button */}
            <IconButton
              onClick={handleDeleteCancel}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                width: 32,
                height: 32,
                borderRadius: "8px",
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "#f59e0b",
                  color: "#ffffff",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>

            {/* SVG Icon */}
            <Box
              component="img"
              src="/src/assets/modals/delete-modal.svg"
              alt="Delete"
              sx={{
                width: 80,
                height: 80,
                objectFit: "contain",
                margin: "0 auto",
                display: "block",
                mb: 2,
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.25rem",
                color: "text.primary",
                textAlign: "center",
                mb: 2,
              }}
            >
              Delete Holiday
            </Typography>

            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
                fontSize: "0.95rem",
                lineHeight: 1.5,
                textAlign: "center",
                mb: 1,
              }}
            >
              Are you sure you want to delete
            </Typography>

            <Typography
              sx={{
                fontWeight: 600,
                color: "primary.main",
                fontSize: "1rem",
                textAlign: "center",
                mb: 2,
                wordBreak: "break-word",
              }}
            >
              "{holidayToDelete?.holiday_name}"
            </Typography>

            <Typography
              sx={{
                color: "text.secondary",
                fontSize: "0.85rem",
                lineHeight: 1.4,
                textAlign: "center",
              }}
            >
              This holiday will be deleted permanently
            </Typography>
          </Box>

          <Box sx={{ p: 3, pt: 0, display: "flex", gap: 2 }}>
            <Button
              onClick={handleDeleteCancel}
              disabled={deleting}
              fullWidth
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1,
                fontSize: "0.875rem",
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb"
                }`,
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={deleting}
              fullWidth
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1,
                fontSize: "0.875rem",
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                color: "#ffffff",
                boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  boxShadow: "0 6px 16px rgba(245, 158, 11, 0.4)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                "&:disabled": {
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  opacity: 0.6,
                },
              }}
            >
              {deleting ? (
                <CircularProgress size={20} sx={{ color: "#ffffff" }} />
              ) : (
                "Delete"
              )}
            </Button>
          </Box>
        </motion.div>
      </Dialog>
    </Box>
  );
};

export default Holiday;
